import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';

const MAX_FILES = 25;
const API_ENDPOINTS = {
    LIST_FILES:    'drivers/list_files/',
    UPLOAD:        'drivers/upload/',
    DELETE_FILE:   'drivers/delete_file/',
    FILE_COLUMNS:  (id)   => `drivers/file_columns/${id}/`,
    DOWNLOAD_FILE: (name) => `drivers/download/?file_name=${encodeURIComponent(name)}`,
    LINK_FILE:     'drivers/link_file/',
};

const Spin = ({ size = 18, color = '#4f46e5' }) => (
    <svg width={size} height={size} fill="none" viewBox="0 0 24 24"
         style={{ flexShrink: 0, animation: 'du-spin 0.9s linear infinite', transformOrigin: 'center' }}>
        <style>{`@keyframes du-spin{to{transform:rotate(360deg)}}`}</style>
        <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="4" style={{ opacity: 0.2 }} />
        <path fill={color} style={{ opacity: 0.8 }}
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

const Alert = ({ type, msg, onClose }) => {
    const colors = type === 'error'
        ? { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', icon: '#ef4444' }
        : { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d', icon: '#22c55e' };
    return (
        <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'10px 12px',
                      background: colors.bg, border: `1px solid ${colors.border}`,
                      borderRadius: 8, marginTop: 12 }}>
            <span style={{ color: colors.icon, flexShrink:0, fontSize:14 }}>{type === 'error' ? '✕' : '✓'}</span>
            <span style={{ fontSize: 13, color: colors.text, flex: 1 }}>{msg}</span>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
                                               color: colors.icon, fontSize:14, padding:0, flexShrink:0 }}>✕</button>
        </div>
    );
};

const Tab = ({ label, active, onClick }) => (
    <button type="button" onClick={onClick} style={{
        padding: '8px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
        border: 'none', borderBottom: active ? '2px solid #4f46e5' : '2px solid transparent',
        background: 'transparent', color: active ? '#4f46e5' : '#6b7280',
        transition: 'color 0.15s, border-color 0.15s',
    }}>
        {label}
    </button>
);

function LinkModal({ file, projects, onLink, onClose, currentProject }) {
    const [selected, setSelected] = useState(currentProject || '');
    const [linking, setLinking]   = useState(false);

    const handleLink = async () => {
        if (!selected) return;
        setLinking(true);
        await onLink(file.name, selected);
        setLinking(false);
    };

    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000,
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ background:'#fff', borderRadius:12, padding:24, width:360,
                          boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
                <div style={{ fontSize:15, fontWeight:600, color:'#111827', marginBottom:4 }}>
                    Link file to a project
                </div>
                <div style={{ fontSize:12, color:'#6b7280', marginBottom:16 }}>
                    {file.name}
                </div>
                <select
                    value={selected}
                    onChange={e => setSelected(e.target.value)}
                    style={{ width:'100%', padding:'8px 10px', fontSize:13, border:'1px solid #d1d5db',
                             borderRadius:8, outline:'none', marginBottom:16, boxSizing:'border-box' }}>
                    <option value="">— Select a project —</option>
                    {projects.map(p => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <button type="button" onClick={onClose}
                            style={{ padding:'7px 16px', fontSize:13, borderRadius:8,
                                     border:'1px solid #e5e7eb', background:'#f9fafb',
                                     color:'#374151', cursor:'pointer' }}>
                        Cancel
                    </button>
                    <button type="button" onClick={handleLink} disabled={!selected || linking}
                            style={{ padding:'7px 16px', fontSize:13, borderRadius:8,
                                     border:'none', background: selected ? '#4f46e5' : '#c7d2fe',
                                     color:'#fff', cursor: selected ? 'pointer' : 'not-allowed',
                                     display:'flex', alignItems:'center', gap:6 }}>
                        {linking ? <><Spin size={13} color="#fff" /> Linking…</> : 'Link'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FileRow({ f, loadingFileId, downloadingFile, linkingFile, onLoad, onDownload, onDelete, onLinkClick, showProject }) {
    return (
        <div onClick={() => !loadingFileId && onLoad(f.id)}
             style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'10px 12px', borderRadius:8, cursor:'pointer',
                      transition:'background 0.1s',
                      background: loadingFileId===f.id ? '#eef2ff' : 'transparent' }}
             onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
             onMouseLeave={e => e.currentTarget.style.background =
                 loadingFileId===f.id ? '#eef2ff' : 'transparent'}
        >
            <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0, flex:1 }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
                     stroke="#9ca3af" strokeWidth="2" style={{ flexShrink:0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, color:'#111827', overflow:'hidden',
                                  textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {f.name}
                    </div>
                    <div style={{ fontSize:11, color:'#9ca3af', marginTop:1 }}>
                        {showProject && f.projectName
                            ? <span style={{ color:'#4f46e5' }}>📁 {f.projectName}</span>
                            : f.uploadedAt}
                    </div>
                </div>
            </div>
            <div style={{ display:'flex', gap:6, flexShrink:0 }} onClick={e => e.stopPropagation()}>
                {loadingFileId === f.id ? (
                    <Spin size={16} />
                ) : (
                    <>
                        {onLinkClick && (
                            <button type="button"
                                onClick={() => onLinkClick(f)}
                                disabled={linkingFile === f.name}
                                title="Link to project"
                                style={{ padding:'4px 8px', fontSize:11, borderRadius:6,
                                         border:'1px solid #d1fae5', background:'#ecfdf5',
                                         color:'#059669', cursor:'pointer' }}>
                                {linkingFile === f.name ? <Spin size={12} color="#059669" /> : '🔗 Link'}
                            </button>
                        )}
                        <button type="button"
                            onClick={() => onDownload(f.name)}
                            disabled={!!downloadingFile}
                            title="Download"
                            style={{ padding:'4px 8px', fontSize:11, borderRadius:6,
                                     border:'1px solid #e0e7ff', background:'#eef2ff',
                                     color:'#4f46e5', cursor:'pointer' }}>
                            {downloadingFile===f.name ? <Spin size={12} color="#4f46e5" /> : '↓'}
                        </button>
                        <button type="button"
                            onClick={() => onDelete(f.name)}
                            title="Delete"
                            style={{ padding:'4px 8px', fontSize:11, borderRadius:6,
                                     border:'1px solid #fee2e2', background:'#fef2f2',
                                     color:'#ef4444', cursor:'pointer' }}>
                            🗑
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

function DriverDataUpload({ setColumns, setFileId, setBinaryColumns, setValueLabels, selectedProject }) {
    const [tab, setTab]                       = useState('existing');
    const [file, setFile]                     = useState(null);
    const [uploadedFiles, setUploadedFiles]   = useState([]);
    const [errorMessage, setErrorMessage]     = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isUploading, setIsUploading]       = useState(false);
    const [isLoadingList, setIsLoadingList]   = useState(true);
    const [downloadingFile, setDownloadingFile] = useState(null);
    const [fileSearch, setFileSearch]         = useState('');
    const [loadingFileId, setLoadingFileId]   = useState(null);
    const [linkModal, setLinkModal]           = useState(null); // file object or null
    const [linkingFile, setLinkingFile]       = useState(null);
    const [projects, setProjects]             = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => { fetchUploadedFiles(); }, []); // eslint-disable-line

    // Fetch project (segment) names for the link modal — same two-step as ProjectSelector
    useEffect(() => {
        const email = localStorage.getItem('username');
        const token = localStorage.getItem('Token');
        const headers = { Authorization: `Token ${token}` };
        api.get('users/cascading_names/', { params: { email }, headers })
            .then(res => {
                const names = res.data || [];
                if (!names.length) return;
                const param = names.join('&name=');
                return api.get(`api/segments/fetch-segments-by-name?name=${param}`, { headers });
            })
            .then(res => {
                if (!res) return;
                const segmentNames = (res.data || [])
                    .map(s => s.segment_name)
                    .filter(Boolean);
                setProjects([...new Set(segmentNames)]);
            })
            .catch(() => {});
    }, []);

    const fetchUploadedFiles = useCallback(async () => {
        setIsLoadingList(true);
        try {
            const res = await api.get(API_ENDPOINTS.LIST_FILES);
            setUploadedFiles(res.data.map(f => ({
                name: f.name,
                id: f.name,
                uploadedAt: f.uploaded_at || '',
                projectName: f.project_name || '',
                columns: [],
                binaryColumns: [],
            })));
        } catch {
            setErrorMessage('Failed to load files.');
            setUploadedFiles([]);
        } finally {
            setIsLoadingList(false);
        }
    }, []);

    const handleFileUpload = async () => {
        setSuccessMessage(''); setErrorMessage('');
        if (!file) { setErrorMessage('Please select a .sav file first.'); return; }
        if (uploadedFiles.length >= MAX_FILES) {
            setErrorMessage(`Max ${MAX_FILES} files. Delete one first.`); return;
        }
        const formData = new FormData();
        formData.append('file', file);
        if (selectedProject) formData.append('project_name', selectedProject);
        setIsUploading(true);
        try {
            const res = await api.post(API_ENDPOINTS.UPLOAD, formData);
            const { file_id, columns = [], binary_columns = [], value_labels = {} } = res.data;
            const entry = {
                name: file.name, id: file_id || file.name,
                columns, binaryColumns: binary_columns, valueLabels: value_labels,
                projectName: selectedProject || '',
            };
            setUploadedFiles(prev => [...prev, { ...entry, id: entry.name }]);
            setColumns(entry.columns);
            setBinaryColumns(entry.binaryColumns);
            setValueLabels(entry.valueLabels);
            setFileId(entry.name);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setSuccessMessage(`"${entry.name}" uploaded and loaded.`);
            setTab('existing');
        } catch (e) {
            setErrorMessage(e.response?.data?.detail || e.response?.data?.error || 'Upload failed.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleLoadFile = async (fileId) => {
        setErrorMessage(''); setSuccessMessage('');
        const sel = uploadedFiles.find(f => f.id === fileId);
        if (!sel) return;
        setLoadingFileId(fileId);
        let data = { ...sel };
        if (!data.columns || data.columns.length === 0) {
            try {
                const res = await api.get(API_ENDPOINTS.FILE_COLUMNS(fileId));
                const { columns = [], binary_columns = [], value_labels = {} } = res.data;
                data = { ...data, columns, binaryColumns: binary_columns, valueLabels: value_labels };
                setUploadedFiles(prev => prev.map(f => f.id === fileId ? data : f));
            } catch {
                setErrorMessage('Failed to load file columns.');
                setLoadingFileId(null);
                return;
            }
        }
        setColumns(data.columns);
        setBinaryColumns(data.binaryColumns || []);
        setValueLabels(data.valueLabels || {});
        setFileId(data.id);
        setSuccessMessage(`"${data.name}" loaded — ${data.columns.length} variables ready.`);
        setLoadingFileId(null);
    };

    const handleFileDelete = async (name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        setErrorMessage(''); setSuccessMessage('');
        try {
            const res = await api.delete(API_ENDPOINTS.DELETE_FILE, { data: { file_name: name } });
            setSuccessMessage(res.data.message || 'Deleted.');
            setUploadedFiles(prev => prev.filter(f => f.name !== name));
        } catch (e) {
            setErrorMessage(e.response?.data?.detail || e.response?.data?.error || 'Delete failed.');
        }
    };

    const handleFileDownload = async (name) => {
        if (downloadingFile) return;
        setDownloadingFile(name);
        try {
            const res = await api.get(API_ENDPOINTS.DOWNLOAD_FILE(name), { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url; a.setAttribute('download', name);
            document.body.appendChild(a); a.click();
            a.parentNode.removeChild(a); URL.revokeObjectURL(url);
        } catch {
            setErrorMessage('Download failed.');
        } finally {
            setDownloadingFile(null);
        }
    };

    const handleLinkFile = async (fileName, projectName) => {
        setLinkingFile(fileName);
        try {
            await api.post(API_ENDPOINTS.LINK_FILE, { file_name: fileName, project_name: projectName });
            setUploadedFiles(prev => prev.map(f =>
                f.name === fileName ? { ...f, projectName } : f
            ));
            setSuccessMessage(`"${fileName}" linked to "${projectName}".`);
            setLinkModal(null);
        } catch (e) {
            setErrorMessage(e.response?.data?.error || 'Link failed.');
        } finally {
            setLinkingFile(null);
        }
    };

    const matchesSearch = f => f.name.toLowerCase().includes(fileSearch.toLowerCase());

    // When project selected: only show files linked to that project
    // When no project: show all files split by linked/orphaned
    const linkedFiles = uploadedFiles.filter(f => {
        if (!matchesSearch(f)) return false;
        if (selectedProject) return f.projectName === selectedProject;
        return !!f.projectName;
    });

    const otherFiles = selectedProject ? [] : uploadedFiles.filter(f =>
        matchesSearch(f) && !f.projectName
    );

    const rowProps = {
        loadingFileId,
        downloadingFile,
        linkingFile,
        onLoad: handleLoadFile,
        onDownload: handleFileDownload,
        onDelete: handleFileDelete,
    };

    return (
        <>
            {linkModal && (
                <LinkModal
                    file={linkModal}
                    projects={projects}
                    onLink={handleLinkFile}
                    onClose={() => setLinkModal(null)}
                    currentProject={selectedProject}
                />
            )}

            <div style={{ background:'#fff', borderRadius:16, border:'1px solid #e5e7eb',
                          boxShadow:'0 1px 3px rgba(0,0,0,0.05)', overflow:'hidden' }}>

                {/* Tabs */}
                <div style={{ display:'flex', borderBottom:'1px solid #e5e7eb', padding:'0 20px' }}>
                    <Tab label="Select Existing File" active={tab==='existing'} onClick={() => setTab('existing')} />
                    <Tab label="Upload New File"      active={tab==='upload'}   onClick={() => setTab('upload')}   />
                </div>

                <div style={{ padding: 20 }}>

                    {errorMessage   && <Alert type="error"   msg={errorMessage}   onClose={() => setErrorMessage('')}   />}
                    {successMessage && <Alert type="success" msg={successMessage} onClose={() => setSuccessMessage('')} />}

                    {/* ── Tab: Select Existing ── */}
                    {tab === 'existing' && (
                        <div style={{ marginTop: errorMessage || successMessage ? 16 : 0 }}>
                            <div style={{ position:'relative', marginBottom:12 }}>
                                <span style={{ position:'absolute', left:10, top:'50%',
                                               transform:'translateY(-50%)', color:'#9ca3af', fontSize:13 }}>🔍</span>
                                <input
                                    type="text"
                                    placeholder="Search files…"
                                    value={fileSearch}
                                    onChange={e => setFileSearch(e.target.value)}
                                    style={{ width:'100%', paddingLeft:30, paddingRight:12, paddingTop:7,
                                             paddingBottom:7, fontSize:13, border:'1px solid #d1d5db',
                                             borderRadius:8, outline:'none', boxSizing:'border-box' }}
                                />
                            </div>

                            {isLoadingList ? (
                                <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}>
                                    <Spin size={24} />
                                </div>
                            ) : (uploadedFiles.length === 0 && !fileSearch) ? (
                                <div style={{ textAlign:'center', padding:'32px 0', color:'#9ca3af', fontSize:13 }}>
                                    No files uploaded yet. Switch to "Upload New File".
                                </div>
                            ) : (
                                <div style={{ maxHeight:400, overflowY:'auto' }}>

                                    {/* Linked / project files */}
                                    {linkedFiles.length > 0 && (
                                        <div>
                                            {selectedProject ? (
                                                <div style={{ fontSize:11, fontWeight:600, color:'#4f46e5',
                                                              textTransform:'uppercase', letterSpacing:'0.05em',
                                                              padding:'4px 0 6px 12px' }}>
                                                    📁 {selectedProject}
                                                </div>
                                            ) : (
                                                <div style={{ fontSize:11, fontWeight:600, color:'#6b7280',
                                                              textTransform:'uppercase', letterSpacing:'0.05em',
                                                              padding:'4px 0 6px 12px' }}>
                                                    Linked Files
                                                </div>
                                            )}
                                            {linkedFiles.map(f => (
                                                <FileRow key={f.id} f={f} {...rowProps}
                                                    showProject={!selectedProject}
                                                    onLinkClick={null}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {/* No file linked to this project */}
                                    {selectedProject && linkedFiles.length === 0 && (
                                        <div style={{ textAlign:'center', padding:'32px 0', color:'#9ca3af', fontSize:13 }}>
                                            No file linked to <strong style={{ color:'#4f46e5' }}>{selectedProject}</strong> yet.
                                            Upload a new file or link an existing one.
                                        </div>
                                    )}

                                    {/* Orphaned files (only when no project selected) */}
                                    {otherFiles.length > 0 && (
                                        <div style={{ marginTop: linkedFiles.length ? 16 : 0 }}>
                                            <div style={{ fontSize:11, fontWeight:600, color:'#f59e0b',
                                                          textTransform:'uppercase', letterSpacing:'0.05em',
                                                          padding:'4px 0 6px 12px', display:'flex',
                                                          alignItems:'center', gap:6 }}>
                                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24"
                                                     stroke="#f59e0b" strokeWidth="2.5">
                                                    <path strokeLinecap="round" strokeLinejoin="round"
                                                          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                                </svg>
                                                Orphaned — not linked to any project
                                            </div>
                                            {otherFiles.map(f => (
                                                <FileRow key={f.id} f={f} {...rowProps}
                                                    showProject={false}
                                                    onLinkClick={f2 => setLinkModal(f2)}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {linkedFiles.length === 0 && otherFiles.length === 0 && !selectedProject && fileSearch && (
                                        <div style={{ textAlign:'center', padding:'32px 0',
                                                      color:'#9ca3af', fontSize:13 }}>
                                            No files match "{fileSearch}"
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Tab: Upload New ── */}
                    {tab === 'upload' && (
                        <div style={{ marginTop: errorMessage || successMessage ? 16 : 8 }}>
                            {selectedProject && (
                                <div style={{ fontSize:12, color:'#4f46e5', background:'#eef2ff',
                                              borderRadius:6, padding:'6px 10px', marginBottom:12 }}>
                                    This file will be linked to <strong>{selectedProject}</strong>
                                </div>
                            )}
                            <div style={{ marginBottom:12 }}>
                                <label style={{ fontSize:13, fontWeight:500, color:'#374151',
                                                display:'block', marginBottom:6 }}>
                                    Select .sav file
                                </label>
                                <input
                                    type="file"
                                    accept=".sav"
                                    ref={fileInputRef}
                                    onChange={e => setFile(e.target.files?.[0] || null)}
                                    disabled={isUploading}
                                    style={{ fontSize:13, color:'#374151', width:'100%' }}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleFileUpload}
                                disabled={isUploading || !file}
                                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                                         width:'100%', padding:'10px 0', fontSize:13, fontWeight:500,
                                         background: (!file || isUploading) ? '#c7d2fe' : '#4f46e5',
                                         color:'#fff', border:'none', borderRadius:8,
                                         cursor: (!file || isUploading) ? 'not-allowed' : 'pointer' }}>
                                {isUploading ? <><Spin size={16} color="#fff" /> Uploading…</> : '↑  Upload File'}
                            </button>
                            <p style={{ fontSize:11, color:'#9ca3af', marginTop:8 }}>
                                Max file size: 100 MB &nbsp;·&nbsp; {uploadedFiles.length}/{MAX_FILES} files used
                            </p>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}

DriverDataUpload.propTypes = {
    setColumns:       PropTypes.func.isRequired,
    setFileId:        PropTypes.func.isRequired,
    setBinaryColumns: PropTypes.func.isRequired,
    setValueLabels:   PropTypes.func.isRequired,
    selectedProject:  PropTypes.string,
};

DriverDataUpload.defaultProps = {
    selectedProject: '',
};

export default DriverDataUpload;
